import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { publicApi, formatApiError } from "@/lib/api";
import { computeLandingUrl } from "@/lib/trackingUrl";
import { Sparkles, MapPin, Mail, User, Phone, ArrowRight, CheckCircle2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const BG = "https://images.pexels.com/photos/8357142/pexels-photo-8357142.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

function _appendQueryParam(url, key, value) {
  if (!url || !value) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}${key}=${encodeURIComponent(value)}`;
}

export default function PublicLink() {
  const { slug } = useParams();
  const [sp] = useSearchParams();
  const productId = sp.get("p");
  const [link, setLink] = useState(null);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [delivery, setDelivery] = useState("");
  const [discount, setDiscount] = useState(null); // true / false
  const [done, setDone] = useState(false);

  const loadLink = React.useCallback(async () => {
    setError(null);
    setNotFound(false);
    try {
      const { data } = await publicApi.get(`/r/${slug}`);
      setLink(data);
      // Short-circuit: when ?p=<product_id> is present, this came from the
      // /share flow. Skip the lead-capture popup and 302-style redirect
      // straight to the destination so the shopper just lands on the product.
      if (productId && data.final_destination_url) {
        // Single source of truth for the landing URL — same helper that
        // SharePage uses for its live preview, so they can't diverge.
        const target = computeLandingUrl({
          finalUrl: data.final_destination_url,
          slug,
          productId,
        });
        window.location.replace(target);
      }
    } catch (e) {
      if (e.response?.status === 404) {
        setNotFound(true);
      } else {
        const status = e.response?.status;
        const detail = formatApiError(e.response?.data?.detail);
        setError(status ? `Network error (${status})${detail ? ": " + detail : ""}` : (e.message || "Couldn't reach the server. Please check your connection and try again."));
      }
    }
  }, [slug, productId]);

  useEffect(() => {
    loadLink();
  }, [loadLink]);

  const onSubmit = async (e) => {
    e.preventDefault();

    // Client-side validation — surface the exact missing field instead of a generic 422
    const missing = [];
    if (!name.trim()) missing.push("name");
    if (!email.trim()) missing.push("email");
    if (!phone.trim() || phone.trim().length < 4) missing.push("phone");
    if (!delivery.trim()) missing.push("delivery location");
    if (discount === null) missing.push("discount choice");
    if (missing.length > 0) {
      toast.error(`Please fill in: ${missing.join(", ")}`);
      return;
    }

    setSubmitting(true);
    try {
      const { data } = await publicApi.post(
        `/r/${slug}/lead`,
        {
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim(),
          delivery_location: delivery.trim(),
          wants_discount: !!discount,
        },
        { headers: link?.click_id ? { "x-click-id": link.click_id } : {} }
      );
      setDone(true);
      setTimeout(() => {
        window.location.href = data.destination_url;
      }, 1400);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      <img src={BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-br from-white/40 via-white/30 to-[#002FA7]/30" />
      <div className="absolute inset-0 bg-grid pointer-events-none" />

      <div className="relative min-h-screen flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md fade-up" data-testid="public-link-card">
          {notFound ? (
            <div className="glass-card rounded-2xl p-8 text-center" data-testid="public-link-not-found">
              <div className="font-display font-black text-3xl mb-2">Link not found</div>
              <p className="text-[#4B5563]">This link doesn't exist or has been removed.</p>
            </div>
          ) : error ? (
            <div className="glass-card rounded-2xl p-8 text-center" data-testid="public-link-error">
              <div className="font-display font-black text-3xl mb-2">Couldn't load this link</div>
              <p className="text-[#4B5563] mb-5">{error}</p>
              <button
                onClick={loadLink}
                data-testid="public-link-retry-button"
                className="btn-primary inline-flex items-center gap-2"
              >
                <RefreshCw size={14} /> Try again
              </button>
            </div>
          ) : !link || productId ? (
            <div className="glass-card rounded-2xl p-8 text-center" data-testid="public-link-redirecting">
              <div className="h-8 w-8 mx-auto rounded-full border-2 border-[#111827] border-t-transparent animate-spin" />
              {productId && <div className="text-xs text-[#4B5563] mt-3">Forwarding you to the shop…</div>}
            </div>
          ) : done ? (
            <div className="glass-card rounded-2xl p-8 text-center" data-testid="public-link-success">
              <CheckCircle2 size={42} className="mx-auto text-[#002FA7] mb-3" />
              <div className="font-display font-black text-3xl mb-2">You're in!</div>
              <p className="text-[#4B5563]">Forwarding you to <span className="font-bold text-[#111827]">{new URL(link.destination_url).hostname}</span>…</p>
            </div>
          ) : (
            <div className="glass-card rounded-2xl p-8">
              <div className="flex items-center gap-2 mb-4">
                <div className="h-8 w-8 rounded-md bg-[#111827] flex items-center justify-center">
                  <span className="font-display font-black text-white text-sm">T</span>
                </div>
                <div className="label-eyebrow text-[#002FA7]">Referred by {link.affiliate_name}</div>
              </div>
              <h1 className="text-3xl sm:text-4xl tracking-tight font-black text-[#111827] leading-tight">
                {link.name}
              </h1>
              {link.discount_offer && (
                <div className="mt-4 flex items-center gap-2 rounded-md bg-[#002FA7] text-white px-3 py-2 text-sm font-bold w-fit">
                  <Sparkles size={14} /> {link.discount_offer}
                </div>
              )}
              <p className="text-sm text-[#4B5563] mt-4">Just a moment — share a few details and we'll send you on your way.</p>

              <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="lead-form">
                <Field icon={User} testid="lead-name-input" required value={name} onChange={setName} placeholder="Your name" />
                <Field icon={Mail} testid="lead-email-input" required type="email" value={email} onChange={setEmail} placeholder="Email address" />
                <Field icon={Phone} testid="lead-phone-input" required type="tel" value={phone} onChange={setPhone} placeholder="Phone number (with country code)" />
                <Field icon={MapPin} testid="lead-delivery-input" required value={delivery} onChange={setDelivery} placeholder="Delivery location (city, country)" />

                <div>
                  <div className="label-eyebrow text-[#4B5563] mb-2">Apply discount?</div>
                  <div className="grid grid-cols-2 gap-2">
                    <button type="button" data-testid="lead-discount-yes" onClick={() => setDiscount(true)} className={`rounded-md border px-4 py-3 text-sm font-bold transition-all ${discount === true ? "bg-[#002FA7] border-[#002FA7] text-white" : "bg-white/80 border-[#E5E7EB] text-[#111827] hover:border-[#111827]"}`}>YES</button>
                    <button type="button" data-testid="lead-discount-no" onClick={() => setDiscount(false)} className={`rounded-md border px-4 py-3 text-sm font-bold transition-all ${discount === false ? "bg-[#111827] border-[#111827] text-white" : "bg-white/80 border-[#E5E7EB] text-[#111827] hover:border-[#111827]"}`}>NO</button>
                  </div>
                  {discount === null && <p className="text-xs text-[#9CA3AF] mt-2">Choose one to continue.</p>}
                </div>

                <button type="submit" disabled={submitting || discount === null} data-testid="lead-submit-button" className="btn-accent w-full">
                  {submitting ? "Sending…" : <>Continue <ArrowRight size={16} /></>}
                </button>
                <p className="text-xs text-[#4B5563] text-center">Your info is shared with {link.affiliate_name} only. No spam.</p>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, testid, value, onChange, placeholder, type = "text", required }) {
  return (
    <div className="relative">
      <Icon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
      <input
        data-testid={testid}
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-md border border-[#E5E7EB] bg-white/80 backdrop-blur-sm pl-9 pr-4 py-2.5 text-sm text-[#111827] outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] placeholder:text-[#9CA3AF]"
      />
    </div>
  );
}
